// Document checklist logic — shared between parent + admin.

import { supabase, isSupabaseConfigured } from "./supabase";

export async function listDocumentTypes(organizationId, { scope = "enrollment" } = {}) {
  if (!isSupabaseConfigured) return [];
  let q = supabase
    .from("document_type_definitions")
    .select("id, code, label, description, is_required, sort_order")
    .order("sort_order", { ascending: true });
  if (organizationId) q = q.eq("organization_id", organizationId);
  if (scope) q = q.eq("scope", scope);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/**
 * Build the checklist rows for a single enrollment record.
 * Returns: [{ type, documents: [doc,…], effectiveStatus }]
 *   effectiveStatus ∈ {missing | uploaded | approved | rejected}
 *
 * Any documents whose document_type_id doesn't match a known type are
 * grouped into a single synthetic "Other uploads" row so we don't leak
 * uuids into the UI.
 */
export function buildChecklistRows(types, documents) {
  const knownTypeIds = new Set((types || []).map((t) => t.id));
  const byType = {};
  for (const d of documents || []) {
    const typed = d.document_type_id && knownTypeIds.has(d.document_type_id);
    const key = typed ? d.document_type_id : "__other__";
    (byType[key] ||= []).push(d);
  }
  const typed = (types || []).map((t) => {
    const docs = byType[t.id] || [];
    return { type: t, documents: docs, effectiveStatus: effective(docs) };
  });
  const others = byType.__other__ || [];
  if (others.length > 0) {
    typed.push({
      type: {
        id: "__other__",
        code: "other_uploads",
        label: "Other uploads",
        description: "Documents that aren't tied to a required type.",
        is_required: false,
        sort_order: 9999,
      },
      documents: others,
      effectiveStatus: effective(others),
    });
  }
  return typed;
}

function effective(docs) {
  if (!docs || docs.length === 0) return "missing";
  // Treat the LATEST document per type as the effective status — the
  // parent may have re-uploaded a new version after a rejection.
  const latest = [...docs].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || ""),
  )[0];
  return latest.status || "uploaded";
}

/**
 * Summarise completion for the readiness strip.
 */
export function summariseChecklist(rows) {
  const totalRequired = rows.filter((r) => r.type?.is_required).length;
  const approved = rows.filter(
    (r) => r.type?.is_required && r.effectiveStatus === "approved",
  ).length;
  const uploaded = rows.filter(
    (r) => r.type?.is_required && r.effectiveStatus === "uploaded",
  ).length;
  const rejected = rows.filter(
    (r) => r.type?.is_required && r.effectiveStatus === "rejected",
  ).length;
  const missing = rows.filter(
    (r) => r.type?.is_required && r.effectiveStatus === "missing",
  ).length;
  return {
    totalRequired,
    approvedCount: approved,
    uploadedCount: uploaded,
    rejectedCount: rejected,
    missingRequired: missing,
    percentComplete:
      totalRequired === 0
        ? 100
        : Math.round(((approved + uploaded) / totalRequired) * 100),
  };
}

export async function reviewDocument({ documentId, status, note }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_review_enrollment_document", {
    p_document_id: documentId,
    p_status: status,
    p_review_note: note || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export function statusTone(status) {
  switch (status) {
    case "approved": return { bg: "bg-primary/10",      color: "text-primary-hover",   ring: "border-primary/30",   label: "Approved" };
    case "uploaded": return { bg: "bg-secondary/30",    color: "text-neutral-900",     ring: "border-secondary/50", label: "Pending review" };
    case "rejected": return { bg: "bg-[#C6851E]/10",    color: "text-[#C6851E]",       ring: "border-[#C6851E]/40", label: "Rejected" };
    case "missing":  return { bg: "bg-neutral-100",     color: "text-neutral-500",     ring: "border-neutral-200",  label: "Missing" };
    default:         return { bg: "bg-neutral-100",     color: "text-neutral-600",     ring: "border-neutral-200",  label: status || "—" };
  }
}
